import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import JumpToNav from '@/components/JumpToNav';
import InstallQR from '@/components/InstallQR';
import { LIFECYCLE_QUADRANTS, LifecycleSlideOver } from '@/components/lifecycle/LifecycleSlideOver';
import {
  Monitor, ShieldCheck, Megaphone, Users2, Calendar, TrendingUp,
  Globe, Users, Brain, LineChart, Briefcase, CalendarRange, QrCode,
  Smartphone, Search, MessageSquare, Dna, Rocket, BookOpen, Share2,
  Lock, CheckCircle2, ArrowRight, Activity, Star, Building2, HandHeart,
  HeartPulse, Stethoscope, Sparkles, Zap, FileText, Newspaper
} from 'lucide-react';

/* ---------- Surgeon + matching doctor variants — randomly toggled each page load ---------- */
const SURGEON_VARIANTS = [
  {
    src: '/hero-surgeon.png',
    alt: 'Specialist surgeon (male) with loupes in operating theatre',
    objectPosition: 'center',
    transform: 'none',
    doctor: {
      initial: 'R',
      avatarBg: 'from-violet-500 to-indigo-600',
      name: 'Dr. Rajesh Sharma',
      specialty: 'Cardiology | 15 yrs exp',
      slug: 'dr-rajesh-sharma',
    },
  },
  {
    src: '/hero-surgeon-female.png',
    alt: 'Specialist surgeon (female) with loupes in operating theatre',
    objectPosition: 'center',
    transform: 'translateX(-22%) scale(1.05)',
    doctor: {
      initial: 'A',
      avatarBg: 'from-pink-500 to-rose-600',
      name: 'Dr. Ananya Iyer',
      specialty: 'Neurosurgery | 12 yrs exp',
      slug: 'dr-ananya-iyer',
    },
  },
];

/* ---------- Equation icons (sharp lucide line-art) ---------- */
const EQUATION_ITEMS = [
  { icon: Monitor, title: 'Get Website + Contents' },
  { icon: ShieldCheck, title: 'Publish Clinical Skills' },
  { icon: Megaphone, title: 'Direct Digital Reach' },
  { icon: Users2, title: '0 Aggregators' },
];

const TOOLS = [
  { icon: Globe, title: 'Website Builder', desc: 'Get your own professional website.' },
  { icon: Users, title: 'Patient Management', desc: 'Manage patients, records & follow-ups.' },
  { icon: Brain, title: 'AI Case Study & Research', desc: 'Build research & publications with AI.' },
  { icon: LineChart, title: 'Visibility Engine', desc: 'Boost your visibility on search engines.' },
  { icon: Briefcase, title: 'Platform Tools', desc: 'Access essential tools for your clinical journey.' },
  { icon: CalendarRange, title: 'Multi-Location Scheduling', desc: 'Schedule across multiple locations.' },
  { icon: QrCode, title: 'Patient QR Auto-Save', desc: 'Save patient details instantly.' },
  { icon: Smartphone, title: 'PWA — Install as App', desc: 'Offer your app-like experience.' },
  { icon: Search, title: 'SEO Pages', desc: 'Auto-generate SEO pages for procedures.' },
  { icon: MessageSquare, title: 'Doctor Chat', desc: 'In-app messaging & patient communication.' },
  { icon: Dna, title: 'DNA Tracking', desc: 'Track no-shows & optimize follow-ups.' },
  { icon: Rocket, title: 'Turbo Visibility Accelerator', desc: 'Boost rankings with guided placements.' },
  { icon: BookOpen, title: 'Medical Insights Hub', desc: 'Share knowledge & build authority.' },
  { icon: Share2, title: 'Patient Referral Tracking', desc: 'Track & manage patient referrals.' },
  { icon: Lock, title: 'Encrypted Data Export', desc: 'Securely export & backup your data.' },
];

const STAGES = [
  {
    icon: HeartPulse, stage: 'STAGE 01', label: 'First Practice',
    bullets: ['Patient Visibility', 'Start Getting Patients'], more: 2,
    accent: 'sky', tint: 'bg-sky-50', ring: 'border-sky-100', icoBg: 'bg-sky-100', icoText: 'text-sky-600', text: 'text-sky-700',
  },
  {
    icon: TrendingUp, stage: 'STAGE 02', label: 'Established Practice',
    bullets: ['Become a Stronger Brand', 'Maximize Patient Inflows'], more: 1,
    accent: 'emerald', tint: 'bg-emerald-50', ring: 'border-emerald-100', icoBg: 'bg-emerald-100', icoText: 'text-emerald-600', text: 'text-emerald-700',
  },
  {
    icon: BookOpen, stage: 'STAGE 03', label: 'Mastery & Innovation',
    bullets: ['Niche High-Complexity Cases', 'Publishing Platform'], more: 1,
    accent: 'amber', tint: 'bg-amber-50', ring: 'border-amber-100', icoBg: 'bg-amber-100', icoText: 'text-amber-600', text: 'text-amber-700',
  },
  {
    icon: Building2, stage: 'STAGE 04', label: 'Public Footprint',
    bullets: ['Global Academic Visibility', 'Global Acclaim'], more: 1,
    accent: 'violet', tint: 'bg-violet-50', ring: 'border-violet-100', icoBg: 'bg-violet-100', icoText: 'text-violet-600', text: 'text-violet-700',
  },
  {
    icon: HandHeart, stage: 'STAGE 05', label: 'Practicing Emeritus',
    bullets: ['Continued Impact & Income', 'Mentorship & Masterclass'], more: 2,
    accent: 'rose', tint: 'bg-rose-50', ring: 'border-rose-100', icoBg: 'bg-rose-100', icoText: 'text-rose-600', text: 'text-rose-700',
  },
];

/* ---------- Local mock header (dark, matches design) ---------- */
function MockHeader({ hidden = false }) {
  if (hidden) return null;
  return (
    <header className="relative z-30 bg-transparent">
      <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-5 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3" data-testid="mock-logo">
          <div className="overflow-hidden flex-shrink-0 bg-white/95 rounded-lg p-1" style={{ height: '3.25rem', width: '3.25rem' }}>
            <img src="/docsite-logo.png" alt="DocSite" className="h-full w-full object-contain" />
          </div>
          <div className="leading-tight">
            <p className="text-white font-bold text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>DocSite</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400 -mt-0.5">For Doctors</p>
          </div>
        </Link>
        <nav className="hidden lg:flex items-center gap-8 text-sm text-slate-200">
          {['Features', 'How It Works', 'For Patients', 'Medical Insights', 'Specialties'].map(n => (
            <a key={n} href="#" className="hover:text-white transition-colors">{n}</a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" className="hidden sm:inline-flex px-5 py-2 rounded-full border border-slate-500/60 text-slate-100 hover:border-slate-300 hover:text-white text-sm font-medium transition-colors" data-testid="mock-login-btn">
            Doctor Login
          </Link>
          <Link to="/register?as=doctor&from=hero_mock" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white text-sm font-semibold shadow-lg shadow-violet-900/30 transition-all" data-testid="mock-cta-header">
            Get Started in 3 Clicks, FREE
          </Link>
        </div>
      </div>
    </header>
  );
}

export default function HeroMockPage({ lockedVariant = null }) {
  // Variant selection:
  //   - lockedVariant='male'  -> always show male  (idx 0) — used in A/B variant B
  //   - lockedVariant='female'-> always show female (idx 1) — used in A/B variant C
  //   - null                  -> random + 9s auto-cycle (preview/QA mode)
  const initialIdx = lockedVariant === 'female' ? 1
                   : lockedVariant === 'male'   ? 0
                   : Math.floor(Math.random() * SURGEON_VARIANTS.length);
  const [variantIdx, setVariantIdx] = useState(initialIdx);
  const [openStage, setOpenStage] = useState(null);
  useEffect(() => {
    if (lockedVariant) return; // no auto-cycle when locked
    const id = setInterval(() => {
      setVariantIdx(i => (i + 1) % SURGEON_VARIANTS.length);
    }, 9000);
    return () => clearInterval(id);
  }, [lockedVariant]);
  const activeDoctor = SURGEON_VARIANTS[variantIdx].doctor;
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>DocSite — Hero Mock (A/B v2)</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      {/* ===================== HERO (DARK) ===================== */}
      <section className="relative overflow-hidden text-white" data-testid="hero-mock-section">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#04101f] via-[#071a36] to-[#0a2148]" />

        {/* SURGEON BACKGROUND — single specialist, right-anchored, fills hero. Toggles male/female. */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden data-testid="mock-surgeon-bg">
          {SURGEON_VARIANTS.map((v, i) => (
            <img
              key={v.src}
              src={v.src}
              alt=""
              onError={(e) => { e.currentTarget.src = '/hero-surgeon.png'; }}
              className={`absolute top-0 right-0 h-full w-[60%] hidden md:block object-cover transition-opacity duration-300 ${variantIdx === i ? 'opacity-100' : 'opacity-0'}`}
              style={{ objectPosition: v.objectPosition, transform: v.transform, transformOrigin: 'center' }}
            />
          ))}
          {/* Mobile: single static image (no overlap to avoid dual bleed) — heavy fade so text is readable */}
          <img
            src={SURGEON_VARIANTS[variantIdx].src}
            alt=""
            onError={(e) => { e.currentTarget.src = '/hero-surgeon.png'; }}
            className="md:hidden absolute top-0 right-0 h-full w-full object-cover opacity-25"
            style={{ objectPosition: '60% center' }}
          />
          {/* Left-to-right fade so left text remains readable */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#04101f] via-[#04101f]/95 sm:via-[#04101f]/85 md:via-[#04101f]/70 to-transparent" />
          {/* Bottom fade into the page */}
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#04101f]" />
          {/* Right-edge subtle vignette */}
          <div className="hidden md:block absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-[#04101f]/40 to-transparent" />
        </div>

        {/* OR lights / faint glow */}
        <div className="absolute -top-32 right-1/3 w-[44rem] h-[44rem] rounded-full bg-sky-500/10 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute bottom-0 -left-40 w-[36rem] h-[36rem] rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" aria-hidden />
        {/* Subtle dotted texture */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #93c5fd 1px, transparent 0)', backgroundSize: '32px 32px' }} aria-hidden />

        {/* Vertical decorative side text — left */}
        <div
          className="hidden xl:block absolute left-3 top-1/2 text-[0.85rem] uppercase tracking-[0.32em] text-emerald-300/40 font-bold pointer-events-none select-none z-10"
          style={{ writingMode: 'vertical-rl', transform: 'translateY(-50%) rotate(180deg)', whiteSpace: 'nowrap', fontFamily: 'Outfit, sans-serif' }}
          aria-hidden
        >
          SPECIALIST CLINICAL SKILLS SPEAK, NOT AD SPENDS
        </div>

        <MockHeader hidden={!!lockedVariant} />

        <div className="relative z-10 w-full px-4 sm:px-6 md:px-12 lg:px-20 xl:px-28 pt-4 md:pt-8 pb-6 md:pb-10">
          <div className="grid lg:grid-cols-[1.25fr_1fr] gap-6 lg:gap-14 items-start">
            {/* LEFT */}
            <div className="min-w-0">
              <h1 className="font-light leading-[1.05] tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.5rem, 2.5vw, 3rem)' }} data-testid="mock-h1">
                Get More Patients.
                <span className="font-semibold text-emerald-400 ml-3 inline-flex items-baseline gap-2 whitespace-nowrap">
                  No Middlemen.
                  <Activity size={20} className="text-emerald-400 self-center" strokeWidth={2.25} />
                </span>
              </h1>
              <p className="mt-2 max-w-xl text-slate-300 leading-relaxed" style={{ fontSize: 'clamp(0.85rem, 0.95vw, 1rem)' }}>
                <span className="text-white font-semibold">DocSite&apos;s powerful Clinical Practice Management Platform</span> helps you grow your practice
                to deliver exceptional patient care.
              </p>

              {/* Equation card — compact horizontal layout, 25% larger */}
              <div className="mt-3 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-white/[0.04] to-white/[0.02] backdrop-blur-sm px-4 py-3 sm:px-5 sm:py-4 md:px-5 md:py-4" data-testid="mock-equation">
                <p className="text-[13px] sm:text-[14px] uppercase tracking-[0.18em] sm:tracking-[0.22em] font-bold text-slate-200 mb-3 leading-snug">
                  Highlight your medical expertise online.
                  <span className="text-emerald-400 ml-1 block sm:inline">Patients are searching for you.</span>
                </p>
                <div className="flex flex-wrap 2xl:flex-nowrap items-center gap-x-2 2xl:gap-x-2.5 gap-y-2">
                  {EQUATION_ITEMS.map((it, i) => {
                    const Ico = it.icon;
                    return (
                      <React.Fragment key={it.title}>
                        <div className="inline-flex items-center gap-2 px-3 py-2 xl:px-3.5 xl:py-2.5 2xl:px-4 2xl:py-3 rounded-lg bg-white/[0.04] border border-white/10 flex-shrink-0">
                          <Ico size={18} className="text-emerald-300 flex-shrink-0 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6" strokeWidth={1.5} />
                          <span className="text-[15px] xl:text-[16px] 2xl:text-[19px] font-semibold text-white whitespace-nowrap">{it.title}</span>
                        </div>
                        {i < EQUATION_ITEMS.length - 1 && (
                          <span className="text-emerald-400/80 font-bold text-xl 2xl:text-2xl px-0.5 flex-shrink-0">{i === EQUATION_ITEMS.length - 2 ? '+' : '×'}</span>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* = connector */}
                <div className="flex items-center gap-3 my-3">
                  <div className="flex-1 h-px bg-emerald-500/30" />
                  <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center text-emerald-300 font-bold text-xl leading-none">=</div>
                  <div className="flex-1 h-px bg-emerald-500/30" />
                </div>

                {/* Outcome row — also bumped 25% */}
                <div className="grid md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
                  <div className="rounded-lg bg-white/[0.04] border border-white/10 px-4 py-3 flex items-center gap-3 min-w-0">
                    <Calendar size={26} className="text-emerald-300 flex-shrink-0" strokeWidth={1.5} />
                    <p className="text-[15px] xl:text-[17px] 2xl:text-[20px] text-white leading-snug">
                      <span className="font-semibold">Get Unlimited Patient Appointments &amp; Consults</span>
                      <span className="block text-slate-400 text-[13px] xl:text-[14px] 2xl:text-[16px] mt-0.5">— overtime, directly, @ 0 Costs</span>
                    </p>
                  </div>
                  <div className="hidden md:flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center text-emerald-300 font-bold text-xl leading-none">+</div>
                  </div>
                  <div className="rounded-lg bg-white/[0.04] border border-white/10 px-4 py-3 flex items-center gap-3 min-w-0">
                    <TrendingUp size={26} className="text-emerald-300 flex-shrink-0" strokeWidth={1.5} />
                    <p className="text-[15px] xl:text-[17px] 2xl:text-[20px] text-white leading-snug">
                      <span className="font-semibold">Get a Future Ready Practice Management Platform</span>
                      <span className="block text-emerald-400 text-[13px] xl:text-[14px] 2xl:text-[16px] font-semibold mt-0.5">At Near 0 Costs, Forever!</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Mobile CTAs */}
              <div className="mt-7 flex flex-wrap gap-3 lg:hidden">
                <Link to="/register?as=doctor&from=hero_mock" className="rounded-full px-7 py-3.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold inline-flex items-center gap-2">
                  Get Started in 3 Clicks, FREE <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            {/* RIGHT — booking card stacked above QR card (normal flow so the
                hero height tracks the actual content, letting the JUMP-TO nav
                sit above the fold). */}
            <div className="flex flex-col items-stretch gap-3 lg:items-end">
              {/* Floating browser-window booking card */}
              <div className="w-full max-w-[22rem] ml-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-20" data-testid="mock-booking-card">
                <div className="bg-slate-100 px-3 py-2 flex items-center gap-2 border-b border-slate-200">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  </div>
                  <span className="text-slate-500 text-[11px] ml-2 font-mono">docsite.com/dr/{activeDoctor.slug}</span>
                </div>
                <div className="p-4 space-y-3 text-slate-900">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${activeDoctor.avatarBg} flex items-center justify-center text-white font-bold text-lg transition-colors duration-300`}>{activeDoctor.initial}</div>
                    <div>
                      <p className="font-semibold text-[15px]" style={{ fontFamily: 'Outfit, sans-serif' }}>{activeDoctor.name}</p>
                      <p className="text-xs text-slate-500">{activeDoctor.specialty}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {['Mon', 'Tue', 'Wed'].map(d => (
                      <div key={d} className="bg-slate-50 border border-slate-200 rounded-lg py-1.5 text-center">
                        <p className="text-[10px] font-semibold text-slate-700">{d}</p>
                        <p className="text-[10px] text-slate-500">10:00–13:00</p>
                      </div>
                    ))}
                  </div>
                  <button className="w-full rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold py-2.5 text-sm shadow" data-testid="mock-book-btn">
                    Book Appointment
                  </button>
                  <div className="flex items-center gap-2 text-[11px] bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-md px-2.5 py-1.5">
                    <CheckCircle2 size={13} />
                    <span>Indexed by Google — showing in search results</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-slate-600">
                    <Stethoscope size={13} />
                    <span>Any Specialization</span>
                  </div>
                </div>
              </div>

              {/* QR install card — sits directly below the booking card in
                  normal flow, capped at the same max-width so the right column
                  stays neat and the hero doesn't grow past one viewport. */}
              <div className="w-full max-w-[22rem] ml-auto flex justify-center lg:justify-end z-10" data-testid="mock-install-qr">
                <InstallQR
                  url="https://docsite.care/"
                  title="Scan to install DocSite"
                  subtitle={'Opens in your phone browser → tap "Install" / "Add to Home Screen"'}
                  testId="hero-mock-install-qr"
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Trust strip */}
          <div className="mt-4 md:mt-5 pt-3 border-t border-white/10 flex flex-wrap items-center gap-x-6 sm:gap-x-10 gap-y-2 text-slate-300 text-xs sm:text-sm" data-testid="mock-trust-strip">
            <span className="flex items-center gap-2"><CheckCircle2 size={15} className="text-emerald-400" /> Free to start</span>
            <span className="flex items-center gap-2"><CheckCircle2 size={15} className="text-emerald-400" /> No credit card</span>
            <span className="flex items-center gap-2"><CheckCircle2 size={15} className="text-emerald-400" /> Live in 5 min</span>
          </div>

          {/* JUMP TO ON THIS PAGE — color-coded section pills */}
          <div className="mt-2" data-testid="mock-jump-nav-wrapper">
            <JumpToNav
              testId="hero-mock-jump-nav"
              className="!mt-0 !pt-0 !border-0 [&_.uppercase]:!text-slate-400"
              items={[
                { icon: Search, label: 'SEO Advantage', href: '#seo-advantage', color: 'rose' },
                { icon: Sparkles, label: 'Practice Lifecycle', href: '#lifecycle', color: 'teal' },
                { icon: Zap, label: 'Features', href: '#features', color: 'sky' },
                { icon: Calendar, label: 'How it works', href: '#how-it-works', color: 'emerald' },
                { icon: Rocket, label: 'TurboCharge', href: '#turbocharge', color: 'violet' },
                { icon: Users, label: 'For Patients', to: '/for-patients', color: 'amber' },
                { icon: Stethoscope, label: 'Try a sample doctor', to: '/dr/dr-rajesh-sharma', color: 'fuchsia' },
                { icon: FileText, label: 'Product Brochure', to: '/brochure', color: 'slate' },
              ]}
            />
          </div>
        </div>
      </section>

      {/* ===================== 15 TOOLS GRID (LIGHT) ===================== */}
      <section id="features" className="bg-white py-20 md:py-24 border-y border-slate-100 scroll-mt-32" data-testid="mock-tools-section">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center mb-14">
            <p className="text-xs md:text-sm uppercase tracking-[0.24em] font-bold text-slate-500">
              The Powerful <span className="text-violet-600 font-extrabold">15</span> Tools Clinical Platform
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-10">
            {TOOLS.map(({ icon: Ico, title, desc }) => (
              <div key={title} className="flex gap-3" data-testid={`mock-tool-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="flex-shrink-0">
                  <Ico size={28} className="text-slate-700" strokeWidth={1.4} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-[15px] font-semibold text-slate-900 leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                  <p className="text-[12.5px] text-slate-500 leading-snug mt-1">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== SEO ADVANTAGE ===================== */}
      <section id="seo-advantage" className="bg-gradient-to-br from-sky-50/80 via-white to-slate-50 py-16 md:py-20 scroll-mt-32" data-testid="mock-seo-advantage-section">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <p className="text-xs uppercase tracking-[0.24em] font-bold text-sky-700 mb-3">SEO Advantage</p>
            <h2 className="tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.875rem, 2.4vw, 2.75rem)' }}>
              Every DocSite page is <span className="text-sky-600 font-semibold">built for Google</span> from day one.
            </h2>
            <p className="text-base text-slate-600 mt-3">
              Auto-generated city+specialty landing pages, rich JSON-LD Schema, instant Bing IndexNow + Google Indexing API push. No SEO consultant required.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {[
              { icon: Search, title: 'Auto-SEO Pages', desc: 'City × Specialty pages auto-generated and indexed.' },
              { icon: Rocket, title: 'Instant Indexing', desc: 'Bing IndexNow + Google Indexing API on every publish.' },
              { icon: Sparkles, title: 'Rich Schema.org', desc: 'Physician, MedicalOrganization, FAQ markup baked in.' },
            ].map(({ icon: Ico, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl border border-sky-100 p-5 shadow-sm" data-testid={`mock-seo-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center mb-3">
                  <Ico size={20} className="text-sky-600" strokeWidth={1.6} />
                </div>
                <h3 className="text-[16px] font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                <p className="text-sm text-slate-600 mt-1.5 leading-snug">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== LIFECYCLE STAGES ===================== */}
      <section id="lifecycle" className="bg-white py-16 md:py-20 scroll-mt-32" data-testid="mock-stages-section">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center mb-12">
            <p className="text-sm md:text-base uppercase tracking-[0.22em] font-bold text-slate-600">
              The First <span className="text-violet-600 font-extrabold">Global</span> Clinical Practice Platform
            </p>
            <p className="mt-2 text-sm md:text-base uppercase tracking-[0.22em] font-bold text-slate-500">
              Covering Every <span className="text-violet-600 font-extrabold">Life Stage</span> of a Successful Clinical Practice
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {STAGES.map((s, idx) => {
              const Ico = s.icon;
              const fullStage = LIFECYCLE_QUADRANTS[idx];
              return (
                <button
                  type="button"
                  key={s.label}
                  onClick={() => setOpenStage(fullStage)}
                  className={`text-left rounded-2xl border ${s.ring} ${s.tint} p-6 flex flex-col group hover:shadow-lg hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-400`}
                  data-testid={`mock-stage-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-11 h-11 rounded-xl ${s.icoBg} flex items-center justify-center`}>
                      <Ico size={20} className={s.icoText} strokeWidth={1.5} />
                    </div>
                    <span className={`text-[10px] uppercase tracking-[0.18em] font-bold ${s.text}`}>{s.stage}</span>
                  </div>
                  <h3 className="text-[17px] font-semibold text-slate-900 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.label}</h3>
                  <ul className="mt-3 space-y-1.5 mb-4">
                    {s.bullets.map(b => (
                      <li key={b} className={`text-[12.5px] font-medium ${s.text} flex items-start gap-2`}>
                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${s.icoBg.replace('bg-', 'bg-').replace('-100', '-500')} flex-shrink-0`} />
                        {b}
                      </li>
                    ))}
                    <li className={`text-[11.5px] ${s.text} opacity-80 italic ml-3.5`}>+ {s.more} more</li>
                  </ul>
                  <span className={`mt-auto text-[12.5px] font-semibold ${s.text} inline-flex items-center gap-1`}>
                    See what you get <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== STATS / PRICING STRIP ===================== */}
      <section className="relative overflow-hidden text-white" data-testid="mock-stats-strip">
        <div className="absolute inset-0 bg-gradient-to-r from-[#04101f] via-[#0a2148] to-[#1e1b4b]" />
        <div className="relative z-10 w-full px-4 sm:px-6 md:px-12 lg:px-20 xl:px-28 py-8 sm:py-10 md:py-12 grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] gap-6 sm:gap-8 lg:gap-10 items-center">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/95 p-1 flex items-center justify-center shadow-md flex-shrink-0">
              <img src="/docsite-logo.png" alt="DocSite" className="h-full w-full object-contain" />
            </div>
            <div>
              <p className="font-semibold text-lg leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Everything You Need.<br />All in One Platform.</p>
              <p className="text-slate-400 text-xs mt-2 max-w-xs leading-snug">Save time, reduce costs, and focus on what matters most — your patients.</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>15</p>
              <p className="text-xs text-slate-400 uppercase tracking-[0.14em] mt-1">Powerful Tools</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>1</p>
              <p className="text-xs text-slate-400 uppercase tracking-[0.14em] mt-1">Unified Platform</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>100%</p>
              <p className="text-xs text-slate-400 uppercase tracking-[0.14em] mt-1">Patient Focused</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-violet-600 to-indigo-700 rounded-2xl p-6 shadow-2xl shadow-violet-900/40 relative overflow-hidden">
            <span className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.18em] font-bold bg-amber-300 text-amber-950 px-2 py-1 rounded-full">Limited Time</span>
            <p className="text-[11px] uppercase tracking-[0.16em] font-bold text-violet-200">One Platform. Unlimited Growth.</p>
            <p className="text-xs text-violet-200 mt-1">Market Average vs. DocSite Price</p>
            <p className="text-violet-300/70 line-through text-sm mt-2">Rs. 73,000 – 1,74,000/mo</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-violet-200/70 line-through text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. 1,999</span>
              <span className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. 999</span>
              <span className="text-violet-200 text-sm">/month</span>
            </div>
            <p className="text-[11px] text-violet-200 mt-2">Locked in at this rate. No setup fees. No hidden costs.</p>
          </div>
        </div>
      </section>

      {/* ===================== TRUST FOOTER ROW ===================== */}
      <section className="bg-white py-7 border-t border-slate-100" data-testid="mock-footer-trust">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 grid grid-cols-1 md:grid-cols-3 gap-5 text-sm">
          <div className="flex items-center gap-3">
            <CheckCircle2 size={20} className="text-emerald-500 flex-shrink-0" />
            <p className="text-slate-700"><span className="font-semibold">Indexed by Google</span> — showing in search results</p>
          </div>
          <div className="flex items-center gap-3 md:justify-center">
            <ShieldCheck size={20} className="text-sky-500 flex-shrink-0" />
            <p className="text-slate-700"><span className="font-semibold">Trusted by Doctors.</span> Loved by Patients.</p>
          </div>
          <div className="flex items-center gap-3 md:justify-end">
            <Lock size={20} className="text-violet-600 flex-shrink-0" />
            <p className="text-slate-700"><span className="font-semibold">HIPAA Ready.</span> Enterprise Grade Security.</p>
          </div>
        </div>
      </section>

      {/* ===================== TURBOCHARGE ===================== */}
      <section id="turbocharge" className="bg-gradient-to-br from-sky-50 via-white to-emerald-50/30 py-20 md:py-24 scroll-mt-32" data-testid="mock-turbocharge-section">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-violet-700 bg-violet-100 px-3 py-1.5 rounded-full mb-4">
              <Rocket size={14} /> TurboCharge Add-On
            </span>
            <h2 className="tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.875rem, 2.4vw, 2.75rem)' }}>
              Skip the learning curve. <span className="text-violet-600 font-semibold">We publish for you.</span>
            </h2>
            <p className="text-base text-slate-600 mt-3">
              Our team drafts 2 AI-assisted articles per month + guided placement across 5 high-authority sites. Compounding Google credibility, on autopilot.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {[
              { icon: Newspaper, title: '2 AI articles / month', desc: 'Drafted from your case studies, reviewed by you before publish.' },
              { icon: LineChart, title: '5-link challenge', desc: 'Guided placements across your GBP, LinkedIn, hospital pages, directories & press.' },
            ].map(({ icon: Ico, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl border border-violet-100 p-5 flex gap-4 shadow-sm" data-testid={`mock-turbo-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="w-11 h-11 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <Ico size={22} className="text-violet-600" strokeWidth={1.6} />
                </div>
                <div>
                  <h3 className="text-[16px] font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                  <p className="text-sm text-slate-600 mt-1 leading-snug">{desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/brochure#turbocharge" className="inline-flex items-center gap-2 rounded-full px-7 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold hover:shadow-lg hover:shadow-violet-900/20 transition-all" data-testid="mock-turbo-cta">
              See TurboCharge Plan <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== HOW IT WORKS — 3 STEP ===================== */}
      <section id="how-it-works" className="bg-slate-50/80 py-20 md:py-24 scroll-mt-32" data-testid="mock-howitworks-section">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-violet-600 mb-3">How It Works</p>
            <h2 className="tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.875rem, 2.4vw, 3rem)' }}>
              15 Minutes a Month. That&apos;s It.
            </h2>
            <p className="text-base text-slate-600 mt-3 max-w-xl mx-auto">
              Set up once, publish monthly, and let the SEO engine work for you.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: '01', title: 'Sign Up & Set Up', desc: 'Create your account in 30 seconds. Add your photo, bio, and procedures.' },
              { num: '02', title: 'Go Live', desc: 'Your SEO-optimized website is live. Share it on WhatsApp, social, or your clinic.' },
              { num: '03', title: 'Grow Organically', desc: 'Publish case studies monthly. Patients find you on Google. Track Your Appointments.' },
            ].map((s, i) => (
              <div key={s.num} data-testid={`mock-step-${i}`}>
                <span className="text-6xl font-bold text-violet-100 block mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.num}</span>
                <h3 className="text-xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA STRIP ===================== */}
      <section className="relative overflow-hidden text-white" data-testid="mock-final-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-[#04101f] via-[#0a2148] to-[#1e1b4b]" />
        <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 xl:px-28 py-20 md:py-24 text-center">
          <h2 className="font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.875rem, 2.6vw, 3.25rem)' }}>
            Ready to Be Found by More Patients?
          </h2>
          <p className="text-slate-300 mt-4 text-lg max-w-xl mx-auto">
            Your medical expertise is the content. Google is the distribution. DocSite is the engine.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/register?as=doctor&from=hero_mock_bottom" className="rounded-full px-8 py-3.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold inline-flex items-center gap-2 text-base hover:shadow-lg hover:shadow-violet-900/40 transition-all" data-testid="mock-final-cta-register">
              Start My DocSite Practice <ArrowRight size={18} />
            </Link>
            <Link to="/brochure" className="rounded-full px-8 py-3.5 bg-white/10 border border-white/20 text-white font-medium hover:bg-white/15 transition-colors text-base" data-testid="mock-final-cta-brochure">
              Compare Plans
            </Link>
          </div>
        </div>
      </section>

      {/* Lifecycle Slide-Over Panel — opens when a stage card is clicked */}
      <LifecycleSlideOver stage={openStage} onClose={() => setOpenStage(null)} />

      {/* Mock-only annotation — hidden when running as A/B variant on / */}
      {!lockedVariant && (
        <div className="fixed bottom-4 right-4 z-50 bg-amber-100 border border-amber-300 text-amber-900 text-xs px-3 py-1.5 rounded-full shadow-lg" data-testid="mock-banner">
          Mock v2 — Hero A/B Preview
        </div>
      )}
    </div>
  );
}

